import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, isRef as _isRef } from "vue"

const _hoisted_1 = {
  class: "d-flex align-items-center justify-content-center",
  style: {"min-height":"100px"}
}

import { computed, ref, watch } from 'vue';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { useQuery } from '@vue/apollo-composable';
import { GetInvitesDocument, InviteType } from '@/gql/graphql';
import { formatDate } from '@/utils/date-utils';
import { truncateText } from '@/utils/string-utils';
import { deepParseDates } from '@/utils/date-utils';
import { PAGING_ITEMS_PER_PAGE, TRUNCATE_TEXT_LENGTH } from '@/constants';
import { useUserStore } from '@/store/user';
import InviteDetail from '@/components/invite/InviteDetail.vue';
import InviteAccept from '@/components/invite/InviteAccept.vue';
import InviteCancel from '@/components/invite/InviteCancel.vue';
import InviteResend from '@/components/invite/InviteResend.vue';
import InviteRevoke from '@/components/invite/InviteRevoke.vue';

interface Props {
  invitedUserId?: number | null;
  inviterUserId?: number | null;
  organizationId?: number | null;
  userRole?: string | null;
  statusPattern?: string | null;
  emailPattern?: string | null;
  showStatus?: boolean;
  adminView?: boolean;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'InvitesList',
  props: {
    invitedUserId: {},
    inviterUserId: {},
    organizationId: {},
    userRole: {},
    statusPattern: {},
    emailPattern: {},
    showStatus: { type: Boolean },
    adminView: { type: Boolean }
  },
  emits: ['invitesUpdated', 'countUpdated'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const userStore = useUserStore();

// --- Props for the component ---
const props = __props;
const emits = __emit;

const invitedView = computed<boolean>(() => {
  if (props.adminView) {
    return false;
  }
  return !!props.invitedUserId || props.emailPattern === userStore.email;
});

const inviterView = computed<boolean>(() => {
  if (props.adminView) {
    return false;
  }
  return !!props.inviterUserId;
});

// --- Query invites ---
const pagingPageCurrent = ref<number>(1);
const pagingItemsPerPage = ref<number>(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return invitesResult.value?.invites?.total || 0;
});

const invites = computed<InviteType[]>(() => {
  const rawList = invitesResult.value?.invites?.invites || [];
  console.debug('Raw invites:', rawList);
  const parsedList = rawList.map((inv) => ({
    ...(deepParseDates(inv) as InviteType),
  }));
  console.debug('Invites:', parsedList);
  return parsedList;
});

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  refetchInvites();
});

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1; // this will trigger the watcher -> requestRefetchTasks
  } else {
    refetchInvites();
  }
}

const {
  result: invitesResult,
  loading: loadingInvitesQuery,
  error: errorInvitesQuery,
  refetch: refetchInvites,
} = useQuery(
  GetInvitesDocument,
  {
    userIdInvited: props.invitedUserId,
    userIdInviter: props.inviterUserId,
    organizationId: props.organizationId,
    userRole: props.userRole,
    emailPattern: props.emailPattern,
    statusPattern: props.statusPattern,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  },
  {
    fetchPolicy: 'no-cache',
  }
);

watch(
  pagingTotalRows,
  (newCount) => {
    console.debug('Emitting countUpdated:', newCount);
    emits('countUpdated', newCount);
  },
  { immediate: true }
);

// Refs to handle the modal & the ID of the invites to be acted upon
const showDetailModal = ref(false);
const showAcceptModal = ref(false);
const showCancelModal = ref(false);
const showResendModal = ref(false);
const showRevokeModal = ref(false);
const selectedInvite = ref<InviteType | null>(null);

// --- Invite table fields (dynamic)  ---
const inviteTableFields = computed(() => {
  const fields = [
    {
      key: 'invitedOrganizationUserRole',
      label: 'Role',
      sortable: false,
      tdClass: 'align-middle',
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      tdClass: 'align-middle',
    },
    {
      key: 'expiresAt',
      label: 'Expires At',
      sortable: false,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  // show status if requested
  if (props.showStatus) {
    fields.splice(3, 0, {
      key: 'status',
      label: 'Status',
      sortable: false,
      tdClass: 'align-middle',
    });
  }

  // admin/invited -> show inviter and org
  if (props.adminView || invitedView.value) {
    fields.splice(0, 0, {
      key: 'inviter',
      label: 'Invited By',
      sortable: false,
      tdClass: 'align-middle',
    });
    fields.splice(0, 0, {
      key: 'organizationName',
      label: 'Organization',
      sortable: false,
      tdClass: 'align-middle',
    });
  }

  // admin/inviter -> show recipient
  if (props.adminView || inviterView.value) {
    fields.splice(0, 0, {
      key: 'invited',
      label: 'Recipient',
      sortable: false,
      tdClass: 'align-middle',
    });
  }

  return fields;
});

// --- Handle mutation requests ---
function handleInvitesUpdated() {
  refetchInvites();
  emits('invitesUpdated');
}

// --- Expose methods to parent component ---
__expose({
  requestSearch,
  pagingTotalRows,
  loadingInvitesQuery,
  errorInvitesQuery,
});

// --- Handlers ---
function acceptButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to accept');
    return;
  }
  selectedInvite.value = invite;
  showAcceptModal.value = true;
}

function cancelButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to cancel');
    return;
  }
  selectedInvite.value = invite;
  showCancelModal.value = true;
}

function resendButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to resend');
    return;
  }
  selectedInvite.value = invite;
  showResendModal.value = true;
}

function revokeButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to revoke');
    return;
  }
  selectedInvite.value = invite;
  showRevokeModal.value = true;
}

function inviteRowClicked(invite: InviteType, index: number, event: Event) {
  if (!invite) {
    console.error('No invite to show details for');
    return;
  }
  console.debug('Invite row clicked:', invite);
  selectedInvite.value = invite;
  showDetailModal.value = true;
}

function handleModalsClosed() {
  // clear the refs
  selectedInvite.value = null;

  // close the modals
  showDetailModal.value = false;
  showAcceptModal.value = false;
  showCancelModal.value = false;
  showResendModal.value = false;
  showRevokeModal.value = false;
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (invites.value.length > 0)
      ? (_openBlock(), _createBlock(_unref(BCard), { key: 0 }, {
          default: _withCtx(() => [
            (pagingItemsPerPage.value > 0 && pagingTotalRows.value > pagingItemsPerPage.value)
              ? (_openBlock(), _createBlock(_unref(BPagination), {
                  key: 0,
                  modelValue: pagingPageCurrent.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pagingPageCurrent).value = $event)),
                  "total-rows": pagingTotalRows.value,
                  "per-page": pagingItemsPerPage.value,
                  pills: "",
                  size: "sm",
                  class: "mb-2"
                }, null, 8, ["modelValue", "total-rows", "per-page"]))
              : _createCommentVNode("", true),
            _createVNode(_unref(BTable), {
              items: invites.value,
              busy: _unref(loadingInvitesQuery),
              fields: inviteTableFields.value,
              "show-empty": "",
              small: "",
              striped: "",
              responsive: "",
              hover: "",
              class: "browser-table",
              onRowClicked: inviteRowClicked
            }, {
              "table-busy": _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    class: "align-middle me-1"
                  }),
                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "ms-1" }, "Loading invites...", -1))
                ])
              ]),
              empty: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createElementVNode("div", { class: "text-center my-5" }, [
                  _createElementVNode("p", { class: "text-muted" }, "No invites found.")
                ], -1)
              ]))]),
              "cell(organizationName)": _withCtx((data) => [
                _createTextVNode(_toDisplayString(data.item.organization?.name), 1)
              ]),
              "cell(invited)": _withCtx((data) => [
                (data.item.invited)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("span", null, _toDisplayString(data.item.invited.givenName) + " " + _toDisplayString(data.item.invited.familyName), 1),
                      _createVNode(_component_font_awesome_icon, {
                        icon: ['fas', 'envelope'],
                        class: "ms-2 text-muted",
                        id: 'icon-invited-email-' + data.item.id
                      }, null, 8, ["id"]),
                      _createVNode(_unref(BTooltip), {
                        target: 'icon-invited-email-' + data.item.id,
                        triggers: "hover"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(data.item.invitedEmail), 1)
                        ]),
                        _: 2
                      }, 1032, ["target"])
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(data.item.invitedEmail), 1)
                    ], 64))
              ]),
              "cell(inviter)": _withCtx((data) => [
                _createElementVNode("span", null, _toDisplayString(data.item.inviter.givenName) + " " + _toDisplayString(data.item.inviter.familyName), 1),
                _createVNode(_component_font_awesome_icon, {
                  icon: ['fas', 'envelope'],
                  class: "ms-2 text-muted",
                  id: `icon-inviter-email-` + data.item.id
                }, null, 8, ["id"]),
                _createVNode(_unref(BTooltip), {
                  target: `icon-inviter-email-` + data.item.id,
                  triggers: "hover"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(data.item.inviter.email), 1)
                  ]),
                  _: 2
                }, 1032, ["target"])
              ]),
              "cell(description)": _withCtx((data) => [
                _createTextVNode(_toDisplayString(_unref(truncateText)(data.item.description, _unref(TRUNCATE_TEXT_LENGTH))), 1)
              ]),
              "cell(id)": _withCtx((data) => [
                _createVNode(_unref(BButtonGroup), null, {
                  default: _withCtx(() => [
                    (__props.adminView || invitedView.value)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 0,
                          variant: "outline-primary",
                          size: "sm",
                          onClick: _withModifiers(($event: any) => (acceptButtonClicked(data.item)), ["prevent"])
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'check'] }),
                            _cache[5] || (_cache[5] = _createTextVNode(" Accept ", -1))
                          ]),
                          _: 1
                        }, 8, ["onClick"]))
                      : _createCommentVNode("", true),
                    (invitedView.value)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 1,
                          variant: "outline-danger",
                          size: "sm",
                          onClick: _withModifiers(($event: any) => (revokeButtonClicked(data.item)), ["prevent"])
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'times'] }),
                            _cache[6] || (_cache[6] = _createTextVNode(" Revoke ", -1))
                          ]),
                          _: 1
                        }, 8, ["onClick"]))
                      : _createCommentVNode("", true),
                    (__props.adminView || inviterView.value)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 2,
                          variant: "outline-secondary",
                          size: "sm",
                          onClick: _withModifiers(($event: any) => (resendButtonClicked(data.item)), ["prevent"])
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'envelope'] }),
                            _cache[7] || (_cache[7] = _createTextVNode(" Resend ", -1))
                          ]),
                          _: 1
                        }, 8, ["onClick"]))
                      : _createCommentVNode("", true),
                    (__props.adminView || inviterView.value)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 3,
                          variant: "outline-danger",
                          size: "sm",
                          onClick: _withModifiers(($event: any) => (cancelButtonClicked(data.item)), ["prevent"])
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'times'] }),
                            _cache[8] || (_cache[8] = _createTextVNode(" Cancel ", -1))
                          ]),
                          _: 1
                        }, 8, ["onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 1
            }, 8, ["items", "busy", "fields"]),
            (pagingItemsPerPage.value > 0 && pagingTotalRows.value > pagingItemsPerPage.value)
              ? (_openBlock(), _createBlock(_unref(BPagination), {
                  key: 1,
                  modelValue: pagingPageCurrent.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pagingPageCurrent).value = $event)),
                  "total-rows": pagingTotalRows.value,
                  "per-page": pagingItemsPerPage.value,
                  pills: "",
                  size: "sm",
                  class: "mb-2"
                }, null, 8, ["modelValue", "total-rows", "per-page"]))
              : _createCommentVNode("", true),
            (selectedInvite.value)
              ? (_openBlock(), _createBlock(InviteDetail, {
                  key: 2,
                  invite: selectedInvite.value,
                  show: showDetailModal.value,
                  onClose: handleModalsClosed
                }, null, 8, ["invite", "show"]))
              : _createCommentVNode("", true),
            (selectedInvite.value)
              ? (_openBlock(), _createBlock(InviteAccept, {
                  key: 3,
                  invite: selectedInvite.value,
                  show: showAcceptModal.value,
                  adminAccess: __props.adminView,
                  onClose: handleModalsClosed,
                  onSuccess: handleInvitesUpdated
                }, null, 8, ["invite", "show", "adminAccess"]))
              : _createCommentVNode("", true),
            (selectedInvite.value)
              ? (_openBlock(), _createBlock(InviteCancel, {
                  key: 4,
                  invite: selectedInvite.value,
                  show: showCancelModal.value,
                  onClose: handleModalsClosed,
                  onSuccess: handleInvitesUpdated
                }, null, 8, ["invite", "show"]))
              : _createCommentVNode("", true),
            (selectedInvite.value)
              ? (_openBlock(), _createBlock(InviteResend, {
                  key: 5,
                  invite: selectedInvite.value,
                  show: showResendModal.value,
                  onClose: handleModalsClosed,
                  onSuccess: handleInvitesUpdated
                }, null, 8, ["invite", "show"]))
              : _createCommentVNode("", true),
            (selectedInvite.value)
              ? (_openBlock(), _createBlock(InviteRevoke, {
                  key: 6,
                  invite: selectedInvite.value,
                  show: showRevokeModal.value,
                  onClose: handleModalsClosed,
                  onSuccess: handleInvitesUpdated
                }, null, 8, ["invite", "show"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(errorInvitesQuery),
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(errorInvitesQuery) ? (errorInvitesQuery).value = $event : null)),
      variant: "danger",
      dismissible: "",
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(errorInvitesQuery)) + " ", 1),
        _createVNode(_unref(BButton), {
          size: "sm",
          variant: "outline-danger",
          onClick: _unref(refetchInvites)
        }, {
          default: _withCtx(() => [
            _cache[9] || (_cache[9] = _createTextVNode(" Retry ", -1)),
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'sync'],
              class: "ms-1"
            })
          ]),
          _: 1
        }, 8, ["onClick"])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})